import React, { useEffect, useRef, useState } from "react";
import "./Sales.scss";

import { IoSearch } from "react-icons/io5";
import { TbAdjustmentsFilled } from "react-icons/tb";
import { IoIosArrowBack, IoMdClose } from "react-icons/io";
import { BsCreditCard } from "react-icons/bs";
import { LuCalendar } from "react-icons/lu";
import { PiGitBranchBold } from "react-icons/pi";
import { MdOutlineFileDownload } from "react-icons/md";
import { IoCloseCircleOutline, IoAddCircleOutline } from "react-icons/io5";

import {
  fetchSalesByUser,
  fetchSalesSummaryByOrganizationAndBranch,
  fetchSalesToDownloadByOrganizationAndBranch,
} from "../../api/sales";
import { useDispatch, useSelector } from "react-redux";
import { format, isToday, isYesterday } from "date-fns";
import Loader from "../../components/Loader/Loader";
import CustomButton from "../../components/CustomButton/CustomButton";
import { PaymentMethods } from "../../constants/paymentMethods";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import InputSelect from "../../components/InputSelect/InputSelect";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaMoneyBillTransfer } from "react-icons/fa6";

import ROUTES from "../../constants/routes";
import CalenderOptions from "../../constants/calenderFilterOptions";
import useOrganizationIsAllowed from "../../helpers/organizationPrevillages";
import { updateOpenSubscriptionAd } from "../../redux/features/subscription-slice";
import { prepareAndDownloadCSV } from "../../helpers/exportSales";
import { toast } from "react-toastify";
import { useViewPortDetector } from "../../helpers/viewPortDetector";
import SalesDetails from "./SalesDetails/SalesDetails";
import PDF from "../PDF/PDF";
import Customer from "../Customer/Customer";
import { FaPlus } from "react-icons/fa6";
import AddCustomer from "../Customer/AddCustomer/AddCustomer";
import {
  loadNextSales,
  loadSalesIfNeeded,
} from "../../redux/features/sales-slice";
import { FiLoader } from "react-icons/fi";
import { updateNetworkStatus } from "../../redux/features/home-slice";
import { Capacitor } from "@capacitor/core";

const statusOptions = [
  { label: "None", value: "none" },
  { label: "Canceled", value: "canceled" },
  { label: "Returned", value: "returned" },
];

const Sales = ({ userDetails }) => {
  const [searchParams] = useSearchParams();
  const saleId = searchParams.get("sale_id");

  const { currentViewPort } = useViewPortDetector();
  const filterRef = useRef(null);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { is_admin } = useSelector((state) => state.userReducer.value);

  const { currency, id: organization_id } = useSelector(
    (state) => state.organizationReducer.value
  );

  const { id: branch_id, branches } = useSelector(
    (state) => state.branchReducer.value
  );

  const { run, steps, activeIndex } = useSelector(
    (state) => state.tourReducer.value
  );

  const {
    sales: orgSales,
    loading: orgSalesLoading,
    loadingNextPage: orgSalesLoadingNextPage,
    lastPage: orgSalesLastPage,
    salesSummary,
    lastParams,
    loadingBackground,
    loadBackgroundFailed,
  } = useSelector((state) => state.saleReducer.value);

  const orgIsAllowedToUseSalesFilter = useOrganizationIsAllowed("sales_filter");

  const [sales, setSales] = useState();
  const [salesArray, setSalesArray] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState([]);
  const [keyword, setKeyword] = useState(saleId || "");

  const [selectedBranchFilter, setSelectedBranchFilter] = useState({
    label: "",
    value: "",
  });
  const [selectedStatusFilter, setSelectedStatusFilter] = useState({
    label: "",
    value: "",
  });
  const [selectedPaymentMethodFilter, setSelectedPaymentMethodFilter] =
    useState([]);
  const [selectedCalenderFilter, setSelectedCalenderFilter] = useState(null);
  const [dates, setDates] = useState({
    start_date: null,
    end_date: null,
  });
  const [branchesOptions, setBranchesOptions] = useState([]);
  const [totalData, setTotalData] = useState({
    amount: 0.0,
    sales: 0,
  });

  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const lastItemRef = useRef(null);
  const limit = process.env.REACT_APP_PAGE_LIMIT;

  const [selectedDesktopTag, setSelectedDesktopTag] = useState("Filter");
  const [selectedSale, setSelectedSale] = useState(null);

  useEffect(() => {
    if (!userDetails) {
      setTotalData({
        amount: salesSummary?.total_revenue,
        sales: salesSummary?.total_sales,
      });
    }
  }, [salesSummary, userDetails]);

  useEffect(() => {
    if (!userDetails) {
      setSalesArray(orgSales);
      setLastPage(orgSalesLastPage);
    }
  }, [userDetails, orgSales, orgSalesLastPage, lastParams]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          pageNumber < lastPage &&
          !loadingNextPage &&
          !loading &&
          !orgSalesLoading &&
          !orgSalesLoadingNextPage
        ) {
          setPageNumber((prevPage) => prevPage + 1);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 1.0,
      }
    );

    const currentRef = lastItemRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [
    pageNumber,
    lastPage,
    loadingNextPage,
    loading,
    orgSalesLoading,
    orgSalesLoadingNextPage,
    lastItemRef.current,
    sales,
  ]);

  const [isScrolling, setIsScrolling] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleScroll = (e) => {
    const currentScrollTop = e.target.scrollTop;

    setIsScrolling(currentScrollTop > lastScrollY);

    setLastScrollY(currentScrollTop);
  };

  useEffect(() => {
    let options = [];

    if (is_admin) {
      options.push({
        label: "All",
        value: "all",
      });
    }
    branches.forEach((el) => {
      options.push({
        label: el?.name,
        value: el?.id,
      });
    });
    let selectedBranch = options.find((el) => el.value === branch_id);
    setSelectedBranchFilter(selectedBranch);
    setBranchesOptions(options);
  }, [branches, branch_id]);

  const truncateText = (sale, maxLength) => {
    const text = sale.products
      ? sale.products.map((product) => product.name).join(", ")
      : "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const groupSalesByDate = (dataArray) => {
    let total_amount = 0.0;
    let total_sales = 0;

    const groups = dataArray.reduce((acc, sale) => {
      const saleDate = new Date(sale.created_at);
      let group = "";

      if (isToday(saleDate)) {
        group = "Today";
      } else if (isYesterday(saleDate)) {
        group = "Yesterday";
      } else {
        group = format(saleDate, "dd/MM/yyyy");
      }

      if (!acc[group]) {
        acc[group] = {
          total_amount: 0.0,
          list: [],
        };
      }

      if (!sale?.deleted_at) {
        let paymentFilter = appliedFilter.find(
          (el) => el.label === "Payment Method"
        );

        if (paymentFilter?.filter?.length) {
          acc[group]["total_amount"] +=
            sale?.sale_payment_methods
              ?.filter((item) =>
                paymentFilter.filter.includes(item.payment_method)
              )
              ?.reduce((sum, item) => sum + Number(item?.amount || 0), 0) || 0;
        } else {
          // acc[group]["total_amount"] += Number(sale?.total_amount) || 0;
          acc[group]["total_amount"] +=
            sale?.sale_payment_methods
              ?.filter((item) => item?.payment_method !== "credit")
              ?.reduce((sum, item) => sum + Number(item?.amount || 0), 0) || 0;
        }
        total_amount += Number(sale?.total_amount) || 0;
        total_sales += 1;
      }
      const payment_method =
        sale?.sale_payment_methods?.length > 1
          ? "split"
          : sale?.sale_payment_methods?.length === 1
          ? PaymentMethods?.find(
              (el) =>
                el?.value === sale?.sale_payment_methods[0]?.payment_method
            )
          : false;

      acc[group]["list"].push({ ...sale, method: payment_method });

      if (!sale?.deleted_at) {
        const returnedProducts = sale?.products?.filter(
          (product) => product?.return && product?.return !== null
        );

        let latestReturnedDate = new Date(sale?.date);
        sale?.products?.forEach((product) => {
          if (
            product?.return &&
            new Date(product?.return?.updated_at) > latestReturnedDate
          ) {
            latestReturnedDate = new Date(product?.return?.updated_at);
          }
        });

        let dupliGroup = "";

        if (isToday(latestReturnedDate)) {
          dupliGroup = "Today";
        } else if (isYesterday(latestReturnedDate)) {
          dupliGroup = "Yesterday";
        } else {
          dupliGroup = format(latestReturnedDate, "dd/MM/yyyy");
        }

        if (!acc[dupliGroup]) {
          acc[dupliGroup] = {
            total_amount: 0.0,
            list: [],
          };
        }

        if (returnedProducts.length > 0) {
          const duplicateSale = {
            ...sale,
            is_return: true,
            products: returnedProducts,
            total_amount: returnedProducts?.reduce((sum, product) => {
              const quantity = Number(product?.return?.quantity || 0);
              const price = Number(product?.pivot?.price || 0);
              const totalQty = Number(product?.pivot?.quantity || 1);
              const discount = Number(product?.pivot?.discount || 0);
              const percentDiscount = Number(sale?.percent_discount || 0);

              const perUnitDiscount = discount / totalQty;
              const discountedUnitPrice =
                (price - perUnitDiscount) * (1 - percentDiscount / 100);

              return sum + quantity * discountedUnitPrice;
            }, 0),
          };

          // acc[dupliGroup]["total_amount"] -= Number(
          //   duplicateSale?.total_amount || 0
          // );

          acc[dupliGroup]["list"].push(duplicateSale);
        }
      }

      return acc;
    }, {});

    return groups;
  };

  const getSales = async (
    filter_type,
    start_date,
    end_date,
    page,
    payment_method,
    selected_branch_id,
    type
  ) => {
    try {
      const response = await fetchSalesByUser(
        userDetails?.id,
        organization_id,
        selected_branch_id || branch_id,
        filter_type || "",
        payment_method || "",
        start_date || "",
        end_date || "",
        keyword,
        page || 1,
        limit,
        type
      );

      setLastPage(response.data?.lastPage || 1);
      setCurrentPageNumber(response?.data?.sales?.current_page || 1);

      return response.data?.sales?.data || [];
    } catch (error) {
      return [];
    }
  };

  const getInitialSales = async (
    filter_type,
    start_date,
    end_date,
    page,
    payment_method,
    selected_branch_id,
    type
  ) => {
    if (userDetails && userDetails?.id) {
      setLoading(true);

      const salesData = await getSales(
        filter_type,
        start_date,
        end_date,
        page,
        payment_method,
        selected_branch_id,
        type
      );
      setSalesArray(salesData);
      setLoading(false);

      fetchSalesSummaryByOrganizationAndBranch(
        organization_id,
        selected_branch_id || branch_id,
        filter_type || "last_30_days",
        payment_method,
        start_date,
        end_date,
        userDetails?.id,
        "",
        type
      )
        .then((res) => {
          setTotalData({
            amount: res.data?.total_revenue,
            sales: res.data.total_sales,
          });
        })
        .catch(() => {});
    } else {
      dispatch(
        loadSalesIfNeeded({
          organization_id,
          branch_id: selected_branch_id || branch_id,
          filter_type: filter_type || "",
          payment_methods: payment_method || "",
          start_date: start_date || "",
          end_date: end_date || "",
          keyword,
          type,
        })
      );
    }
    setSelectedSale(null);
    setSelectedDesktopTag("Filter");
  };

  const getNextPageSales = async (
    filter_type,
    start_date,
    end_date,
    payment_method,
    selected_branch_id,
    type
  ) => {
    if (userDetails && userDetails?.id) {
      setLoadingNextPage(true);
      const salesData = await getSales(
        filter_type,
        start_date,
        end_date,
        pageNumber,
        payment_method,
        selected_branch_id,
        type
      );
      setSalesArray((prev) => [...prev, ...salesData]);
      setLoadingNextPage(false);
    } else {
      dispatch(loadNextSales({ page: pageNumber }));
    }
  };

  useEffect(() => {
    if (salesArray?.length > 0) {
      const updatedSalesArray = salesArray.map((item, index) => ({
        ...item,
        lastItem: index === salesArray.length - 1,
      }));
      const grouped_sales = groupSalesByDate(updatedSalesArray);
      setSales(grouped_sales);
    } else {
      setSales();
    }
  }, [salesArray]);

  useEffect(() => {
    let dateFilter = appliedFilter.find((el) => el.label === "Calender");
    const dateFilterTag =
      dateFilter && dateFilter?.filter ? dateFilter?.filter : "";

    let branchFilter = appliedFilter.find((el) => el.label === "Branch");
    const branchFilterTag =
      branchFilter && branchFilter?.filter ? branchFilter?.filter : "";

    let paymentFilter = appliedFilter.find(
      (el) => el.label === "Payment Method"
    );
    const paymentFilterTag =
      paymentFilter && paymentFilter?.filter
        ? JSON.stringify(paymentFilter?.filter)
        : "";

    let typeFilter = appliedFilter.find((el) => el.label === "Type");
    const typeFilterTag =
      typeFilter && typeFilter?.filter ? typeFilter?.filter : "";

    getInitialSales(
      dateFilterTag,
      dates.start_date,
      dates.end_date,
      1,
      paymentFilterTag,
      branchFilterTag,
      typeFilterTag
    );
    setPageNumber(1);
  }, [organization_id, branch_id, keyword, appliedFilter, userDetails]);

  useEffect(() => {
    if (lastPage > currentPageNumber && pageNumber > currentPageNumber) {
      let dateFilter = appliedFilter.find((el) => el.label === "Calender");
      const dateFilterTag =
        dateFilter && dateFilter?.filter ? dateFilter?.filter : "";

      let branchFilter = appliedFilter.find((el) => el.label === "Branch");
      const branchFilterTag =
        branchFilter && branchFilter?.filter ? branchFilter?.filter : "";

      let paymentFilter = appliedFilter.find(
        (el) => el.label === "Payment Method"
      );
      const paymentFilterTag =
        paymentFilter && paymentFilter?.filter
          ? JSON.stringify(paymentFilter?.filter)
          : "";

      let typeFilter = appliedFilter.find((el) => el.label === "Type");
      const typeFilterTag =
        typeFilter && typeFilter?.filter ? typeFilter?.filter : "";

      getNextPageSales(
        dateFilterTag,
        dates.start_date,
        dates.end_date,
        paymentFilterTag,
        branchFilterTag,
        typeFilterTag
      );
    }
  }, [pageNumber]);

  const handleAddFilter = () => {
    const filters = [];

    if (selectedCalenderFilter) {
      if (selectedCalenderFilter === "start_end_date") {
        filters.push({
          label: "Calender",
          value: `Start End Dates`,
          filter: selectedCalenderFilter,
          dates: dates,
        });
      } else {
        const selectedCalenderObj = CalenderOptions.find(
          (el) => el.value === selectedCalenderFilter
        );
        filters.push({
          label: "Calender",
          value: `${selectedCalenderObj?.label}`,
          filter: selectedCalenderFilter,
        });
      }
    }
    if (selectedPaymentMethodFilter.length > 0) {
      filters.push({
        label: "Payment Method",
        value: `${selectedPaymentMethodFilter.length} selected`,
        filter: selectedPaymentMethodFilter,
      });
    }

    if (selectedStatusFilter.value && selectedStatusFilter.value !== "none") {
      filters.push({
        label: "Type",
        value: `${selectedStatusFilter.label}`,
        filter: selectedStatusFilter.value,
      });
    }
    if (selectedBranchFilter.value) {
      if (filters.length > 0 || selectedBranchFilter.value !== branch_id) {
        filters.push({
          label: "Branch",
          value: `${selectedBranchFilter.label}`,
          filter: selectedBranchFilter.value,
        });
      }
    }

    setAppliedFilter(filters);
    setOpenFilter(false);
  };

  const removeFilter = (index) => {
    const tempArray = [...appliedFilter];
    if (tempArray[index]) {
      if (tempArray[index]?.label === "Calender") {
        setDates({
          start_date: null,
          end_date: null,
        });
        setSelectedCalenderFilter(null);
      } else if (tempArray[index]?.label === "Branch") {
        let selectedBranch = branchesOptions.find(
          (el) => el.value === branch_id
        );
        setSelectedBranchFilter(selectedBranch);
      } else if (tempArray[index]?.label === "Payment Method") {
        setSelectedPaymentMethodFilter([]);
      } else if (tempArray[index]?.label === "Type") {
        setSelectedStatusFilter({
          label: "",
          value: "",
        });
      } else if (tempArray[index] === "Search") {
        setKeyword("");
      }
    }
    tempArray.splice(index, 1);
    if (tempArray.length === 1) {
      if (selectedBranchFilter?.value === branch_id) {
        setAppliedFilter([]);
      } else {
        setAppliedFilter(tempArray);
      }
    } else {
      setAppliedFilter(tempArray);
    }
  };

  const [maxHeight, setMaxHeight] = useState(0);

  useEffect(() => {
    if (filterRef && filterRef.current) {
      setMaxHeight(filterRef.current.clientHeight);
    }
  }, [filterRef, appliedFilter]);

  const [downloading, setDownloading] = useState(false);

  const handleDownload = () => {
    if (downloading) {
      return;
    }
    setDownloading(true);
    let dateFilter = appliedFilter.find((el) => el.label === "Calender");
    const dateFilterTag =
      dateFilter && dateFilter?.filter ? dateFilter?.filter : "";

    let branchFilter = appliedFilter.find((el) => el.label === "Branch");
    const branchFilterTag =
      branchFilter && branchFilter?.filter ? branchFilter?.filter : "";

    let paymentFilter = appliedFilter.find(
      (el) => el.label === "Payment Method"
    );
    const paymentFilterTag =
      paymentFilter && paymentFilter?.filter
        ? JSON.stringify(paymentFilter?.filter)
        : "";

    let typeFilter = appliedFilter.find((el) => el.label === "Type");
    const typeFilterTag =
      typeFilter && typeFilter?.filter ? typeFilter?.filter : "";

    fetchSalesToDownloadByOrganizationAndBranch(
      organization_id,
      branchFilterTag || branch_id,
      dateFilterTag,
      paymentFilterTag,
      dates.start_date,
      dates.end_date,
      userDetails?.id || "",
      typeFilterTag
    )
      .then(async (res) => {
        if (res.data?.sales?.length > 0) {
          const blob = prepareAndDownloadCSV(res.data?.sales, "sales");
          
          if (Capacitor.isNativePlatform()) {
            const csvBlob = new Blob([blob], { type: "text/csv;charset=utf-8;" });
            const csvUrl = window.URL.createObjectURL(csvBlob);
            window.open(csvUrl, "_blank");
            window.URL.revokeObjectURL(csvUrl);
          } else {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "sales.csv";
            link.click();
            window.URL.revokeObjectURL(url);
          }

          toast.dismiss();
          toast.success("Sales' CSV download started.");
        } else {
          setDownloading(false);
          toast.dismiss();
          toast.info("No data to export.");
        }
      })
      .catch((err) => {})
      .finally(() => {
        setDownloading(false);
      });
  };

  useEffect(() => {
    if (selectedSale?.id && !selectedSale?.is_return) {
      const previous_sale = salesArray?.find(
        (el) => el?.id && el?.id === selectedSale?.id
      );

      if (previous_sale && previous_sale !== selectedSale) {
        setSalesArray((prev) => {
          const newSales = prev?.map((item) =>
            item?.id === selectedSale?.id ? selectedSale : item
          );

          return newSales;
        });
      }
    }
  }, [selectedSale]);

  const resetFilterToDefault = () => {
    setAppliedFilter([]);
    let selectedBranch = branchesOptions.find((el) => el.value === branch_id);
    setSelectedBranchFilter(selectedBranch);
    setSelectedCalenderFilter(null);
    setSelectedPaymentMethodFilter([]);
    setSelectedStatusFilter({
      label: "",
      value: "",
    });
    setDates({
      start_date: null,
      end_date: null,
    });
  };

  useEffect(() => {
    if (loadingBackground) {
      dispatch(updateNetworkStatus({ status: "syncing" }));
    }
  }, [loadingBackground]);

  return (
    <>
      {(loading || orgSalesLoading) && <Loader />}
      <div className="page-with-side-components">
        <div className={`sales`}>
          {!userDetails && (
            <div className="desktop-navbar box-shadow">
              <p>{ROUTES.sales.title}</p>
              <div className="last-tag-svg">
                <TbAdjustmentsFilled
                  onClick={() => {
                    setSelectedDesktopTag("Filter");
                    setSelectedSale(null);
                  }}
                />
              </div>
            </div>
          )}
          <div className={`sales-page-content ${openFilter ? "inactive" : ""}`}>
            <div className="sales-search" ref={filterRef}>
              {appliedFilter.length > 0 ? (
                <div className="applied-filters">
                  <div className="heading">
                    <TbAdjustmentsFilled />
                    <p>Filters used</p>
                  </div>
                  <div className="flex-container">
                    {appliedFilter.map((el, i) => {
                      return (
                        <div className="item" key={i}>
                          <p>
                            {el?.label} : {el?.value}
                          </p>
                          <IoCloseCircleOutline
                            onClick={() => {
                              removeFilter(i);
                            }}
                          />
                        </div>
                      );
                    })}
                    <div
                      className="item add-more"
                      onClick={() => {
                        setOpenFilter(true);
                      }}
                    >
                      <p>Add More</p>
                      <IoAddCircleOutline />
                    </div>

                    <div
                      className="item clear-all"
                      onClick={resetFilterToDefault}
                    >
                      <p>Clear All</p>
                    </div>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setKeyword(e.target.keyword.value);
                  }}
                  className="inputs"
                >
                  <input
                    type="text"
                    name="keyword"
                    defaultValue={keyword}
                    placeholder="Search by item, price or code."
                  />
                  <button type="submit">
                    <IoSearch />
                  </button>
                </form>
              )}
            </div>

            <div
              className="sales-list container-wrapper"
              style={
                userDetails
                  ? { maxHeight: `calc(var(--vh) - 127px - ${maxHeight}px)` }
                  : { maxHeight: `calc(var(--vh) - 50px - ${maxHeight}px)` }
              }
              onScroll={handleScroll}
            >
              {sales ? (
                Object.keys(sales).map((group, i) => {
                  const isLastGroup = i === Object.keys(sales).length - 1;
                  return (
                    <div className="sales-group" key={i}>
                      <div className="heading">
                        <p className="head-title">{group}</p>
                        <p className="total">
                          Total:{" "}
                          <span>
                            {currency}
                            {Number(sales[group]["total_amount"] || 0).toFixed(
                              2
                            )}
                          </span>
                        </p>
                      </div>
                      <div className="lists">
                        {sales[group]["list"]?.map((saleItem, i) => {
                          if (saleItem?.lastItem) {
                            return (
                              <div
                                className={`${
                                  selectedSale?.id === saleItem?.id
                                    ? "highlight-selected-item"
                                    : ""
                                }`}
                                ref={lastItemRef}
                                style={{ width: "100%" }}
                                key={i}
                              >
                                <div
                                  onClick={() => {
                                    if (!saleItem?.is_return) {
                                      if (currentViewPort === "desktop") {
                                        setSelectedDesktopTag("Sales Details");
                                        setSelectedSale(saleItem);
                                      } else {
                                        navigate(ROUTES.salesDetails.path, {
                                          state: {
                                            item: { ...saleItem, method: "" },
                                            userData: userDetails,
                                            isOrder: false,
                                            customerData: null,
                                          },
                                        });
                                      }
                                    }
                                  }}
                                  className={`sale-item ${
                                    saleItem?.deleted_at
                                      ? "deleted"
                                      : saleItem?.is_return
                                      ? "returned"
                                      : ""
                                  }`}
                                >
                                  <div className="left">
                                    <p className="total">
                                      {currency}
                                      {Number(saleItem?.total_amount).toFixed(
                                        2
                                      )}{" "}
                                      {saleItem?.method ? (
                                        saleItem?.method === "split" ? (
                                          <FaMoneyBillTransfer />
                                        ) : (
                                          saleItem?.method?.icon
                                        )
                                      ) : (
                                        ""
                                      )}
                                    </p>
                                    <p className="user">
                                      {saleItem?.customer
                                        ? `👤 ${saleItem?.customer?.name}`
                                        : ""}
                                    </p>
                                    <p className="product-names">
                                      {truncateText(saleItem, 25)}
                                    </p>
                                  </div>

                                  <div className="right">
                                    <p className="time">
                                      {format(
                                        new Date(saleItem?.created_at),
                                        "p"
                                      )}
                                    </p>
                                    <p className="sale-id-web">
                                      {saleItem?.deleted_at
                                        ? "Canceled"
                                        : saleItem?.is_return
                                        ? "Returned"
                                        : ""}{" "}
                                      # {saleItem?.sale_id}
                                    </p>
                                    <p className="time">
                                      Sold by:{" "}
                                      {`${saleItem?.user?.name}`.split(" ")[0]}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          } else {
                            return (
                              <div
                                onClick={() => {
                                  if (!saleItem?.is_return) {
                                    if (currentViewPort === "desktop") {
                                      setSelectedDesktopTag("Sales Details");
                                      setSelectedSale(saleItem);
                                    } else {
                                      navigate(ROUTES.salesDetails.path, {
                                        state: {
                                          item: { ...saleItem, method: "" },
                                          userData: userDetails,
                                          isOrder: false,
                                          customerData: null,
                                        },
                                      });
                                    }
                                  }
                                }}
                                className={`sale-item ${
                                  saleItem?.deleted_at
                                    ? "deleted"
                                    : saleItem?.is_return
                                    ? "returned"
                                    : ""
                                } ${
                                  selectedSale?.id === saleItem?.id
                                    ? "highlight-selected-item"
                                    : ""
                                }`}
                                key={i}
                              >
                                <div className="left">
                                  <p className="total">
                                    {currency}
                                    {Number(saleItem?.total_amount).toFixed(
                                      2
                                    )}{" "}
                                    {saleItem?.method ? (
                                      saleItem?.method === "split" ? (
                                        <FaMoneyBillTransfer />
                                      ) : (
                                        saleItem?.method?.icon
                                      )
                                    ) : (
                                      ""
                                    )}
                                  </p>
                                  <p className="user">
                                    {saleItem?.customer
                                      ? `👤 ${saleItem?.customer?.name}`
                                      : ""}
                                  </p>
                                  <p className="product-names">
                                    {truncateText(saleItem, 25)}
                                  </p>
                                </div>

                                <div className="right">
                                  <p className="time">
                                    {format(
                                      new Date(saleItem?.created_at),
                                      "p"
                                    )}
                                  </p>
                                  <p className="sale-id-web">
                                    {saleItem?.deleted_at
                                      ? "Canceled"
                                      : saleItem?.is_return
                                      ? "Returned"
                                      : ""}{" "}
                                    # {saleItem?.sale_id}
                                  </p>
                                  <p className="time">
                                    Sold by:{" "}
                                    {`${saleItem?.user?.name}`.split(" ")[0]}
                                  </p>
                                </div>
                              </div>
                            );
                          }
                        })}
                        {(loadingNextPage || orgSalesLoadingNextPage) &&
                          isLastGroup && (
                            <div className="loading-next-sales-items">
                              <div className="left">
                                <div className="total"></div>
                                <div className="user"></div>
                                <div className="product-names"></div>
                              </div>
                              <div className="right">
                                <div className="time"></div>
                                <div className="sale-id-web"></div>
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div
                  className="no-sales"
                  style={{
                    maxHeight: `calc(var(--vh) - 50px - ${maxHeight}px)`,
                    minHeight: `calc(var(--vh) - 50px - ${maxHeight}px)`,
                  }}
                >
                  <img src="/images/search.svg" alt="" aria-label="No Items" />
                  <p>You haven't made a sale during this period</p>
                  <span>Questions ? Learn more about this feature</span>
                </div>
              )}
            </div>
          </div>

          <div
            className={`filter-page-content container-wrapper ${
              openFilter ? "active" : ""
            }`}
          >
            <DateFilter
              dispatch={dispatch}
              dates={dates}
              setDates={setDates}
              selectedCalenderFilter={selectedCalenderFilter}
              setSelectedCalenderFilter={setSelectedCalenderFilter}
              orgIsAllowedToUseSalesFilter={orgIsAllowedToUseSalesFilter}
            />
            <MethodFilter
              selectedPaymentMethodFilter={selectedPaymentMethodFilter}
              setSelectedPaymentMethodFilter={setSelectedPaymentMethodFilter}
            />
            <OtherFilter
              setSelectedBranchFilter={setSelectedBranchFilter}
              setSelectedStatusFilter={setSelectedStatusFilter}
              selectedStatusFilter={selectedStatusFilter}
              selectedBranchFilter={selectedBranchFilter}
              branchesOptions={branchesOptions}
            />
          </div>

          <div
            className={`open-filter-icon ${
              userDetails && selectedDesktopTag === "Filter" ? "active" : ""
            }`}
            id="tour-sales-filter"
          >
            <TbAdjustmentsFilled
              onClick={() => {
                setOpenFilter((pre) => !pre);
              }}
            />
          </div>
          {openFilter && (
            <div className="close-filter-icon">
              <IoMdClose
                onClick={() => {
                  setOpenFilter(false);
                }}
              />
              <p>Filter</p>
            </div>
          )}
          {openFilter && (
            <div className="floating-button">
              <CustomButton
                name={`Apply Filter`}
                type={"button"}
                color={"primary"}
                bigButton={true}
                handleSubmit={handleAddFilter}
              />
            </div>
          )}

          {!openFilter && (
            <div
              className={`download-sales-csv ${isScrolling ? "inactive" : ""}`}
            >
              <p>
                {currency}
                {totalData.amount} from {totalData.sales} sales (
                {CalenderOptions.find(
                  (el) => el.value === selectedCalenderFilter
                )?.label || "Last 30 days"}
                )
              </p>
              <div
                className="svg"
                onClick={handleDownload}
                id="tour-sales-export"
              >
                {downloading ? (
                  <FiLoader className="loader" />
                ) : (
                  <MdOutlineFileDownload />
                )}
              </div>
            </div>
          )}
        </div>

        {!userDetails && (
          <div className={`right-desktop-view`}>
            <div className="desktop-navbar box-shadow border-bottom">
              {selectedDesktopTag !== "Filter" && (
                <div
                  className="back"
                  onClick={() => {
                    if (
                      selectedDesktopTag === "PDF" ||
                      selectedDesktopTag === "Select Customer For Sale" ||
                      selectedDesktopTag === "Add Customer For Sale"
                    ) {
                      setSelectedDesktopTag("Sales Details");
                    } else {
                      setSelectedDesktopTag("Filter");
                      setSelectedSale(null);
                    }
                  }}
                >
                  <IoIosArrowBack />
                </div>
              )}
              <p>{selectedDesktopTag}</p>
              {selectedDesktopTag === "Select Customer For Sale" && (
                <div
                  className="last-tag-svg"
                  onClick={() => {
                    setSelectedDesktopTag("Add Customer For Sale");
                  }}
                >
                  <FaPlus />
                </div>
              )}
            </div>

            {selectedDesktopTag === "Filter" &&
              (run
                ? !(
                    steps?.length > 0 &&
                    (steps[activeIndex]?.selector === "#tour-cancel-button" ||
                      steps[activeIndex]?.selector === "#tour-return-button")
                  )
                : true) && (
                <div
                  className={`filter-page-content container-wrapper active side-view `}
                  id="tour-sales-filter-desktop"
                >
                  <DateFilter
                    dispatch={dispatch}
                    dates={dates}
                    setDates={setDates}
                    selectedCalenderFilter={selectedCalenderFilter}
                    setSelectedCalenderFilter={setSelectedCalenderFilter}
                    orgIsAllowedToUseSalesFilter={orgIsAllowedToUseSalesFilter}
                  />
                  <MethodFilter
                    selectedPaymentMethodFilter={selectedPaymentMethodFilter}
                    setSelectedPaymentMethodFilter={
                      setSelectedPaymentMethodFilter
                    }
                  />
                  <OtherFilter
                    setSelectedBranchFilter={setSelectedBranchFilter}
                    setSelectedStatusFilter={setSelectedStatusFilter}
                    selectedStatusFilter={selectedStatusFilter}
                    selectedBranchFilter={selectedBranchFilter}
                    branchesOptions={branchesOptions}
                  />
                  <div className="floating-button">
                    <CustomButton
                      name={`Apply Filter`}
                      type={"button"}
                      color={"primary"}
                      bigButton={true}
                      handleSubmit={handleAddFilter}
                    />
                  </div>
                </div>
              )}

            {(selectedDesktopTag === "Sales Details" ||
              (selectedDesktopTag === "Filter" && run
                ? steps?.length > 0 &&
                  (steps[activeIndex]?.selector === "#tour-cancel-button" ||
                    steps[activeIndex]?.selector === "#tour-return-button")
                : false)) && (
              <SalesDetails
                desktopView={true}
                sale={selectedSale}
                setSelectedDesktopTag={setSelectedDesktopTag}
                setSelectedSale={setSelectedSale}
              />
            )}

            {selectedDesktopTag === "PDF" && <PDF id={selectedSale?.id} />}

            {selectedDesktopTag === "Select Customer For Sale" && (
              <Customer
                isSale={true}
                sale={selectedSale}
                desktopView={true}
                setSelectedDesktopTag={setSelectedDesktopTag}
                setSelectedSale={setSelectedSale}
              />
            )}

            {selectedDesktopTag === "Add Customer For Sale" && (
              <AddCustomer
                isSale={true}
                sale={selectedSale}
                desktopView={true}
                setSelectedDesktopTag={setSelectedDesktopTag}
                setSelectedSale={setSelectedSale}
              />
            )}
          </div>
        )}
      </div>

      {userDetails && (
        <div
          className={`right-desktop-view absolute ${
            selectedDesktopTag === "Filter" ? "" : "active"
          }`}
        >
          <div className="desktop-navbar">
            {selectedDesktopTag !== "Filter" && (
              <div
                className="back"
                onClick={() => {
                  if (
                    selectedDesktopTag === "PDF" ||
                    selectedDesktopTag === "Select Customer For Sale" ||
                    selectedDesktopTag === "Add Customer For Sale"
                  ) {
                    setSelectedDesktopTag("Sales Details");
                  } else {
                    setSelectedDesktopTag("Filter");
                  }
                }}
              >
                <IoIosArrowBack />
              </div>
            )}
            <p>{selectedDesktopTag}</p>
            {selectedDesktopTag === "Select Customer For Sale" && (
              <div
                className="last-tag-svg"
                onClick={() => {
                  setSelectedDesktopTag("Add Customer For Sale");
                }}
              >
                <FaPlus />
              </div>
            )}
          </div>

          {selectedDesktopTag === "Sales Details" && (
            <SalesDetails
              desktopView={true}
              sale={selectedSale}
              setSelectedDesktopTag={setSelectedDesktopTag}
            />
          )}

          {selectedDesktopTag === "PDF" && <PDF id={selectedSale?.id} />}

          {selectedDesktopTag === "Select Customer For Sale" && (
            <Customer
              isSale={true}
              sale={selectedSale}
              desktopView={true}
              setSelectedDesktopTag={setSelectedDesktopTag}
              setSelectedSale={setSelectedSale}
            />
          )}

          {selectedDesktopTag === "Add Customer For Sale" && (
            <AddCustomer
              isSale={true}
              sale={selectedSale}
              desktopView={true}
              setSelectedDesktopTag={setSelectedDesktopTag}
              setSelectedSale={setSelectedSale}
            />
          )}
        </div>
      )}
    </>
  );
};

export default Sales;

const DateFilter = ({
  dispatch,
  dates,
  setDates,
  selectedCalenderFilter,
  setSelectedCalenderFilter,
  orgIsAllowedToUseSalesFilter,
}) => {
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDates({ ...dates, [name]: value });
  };

  useEffect(() => {
    if (dates.start_date && dates.end_date) {
      setSelectedCalenderFilter("start_end_date");
    } else if (dates.start_date || dates.end_date) {
      setSelectedCalenderFilter(null);
    }
  }, [dates]);

  const clearDates = () => {
    setDates({
      start_date: null,
      end_date: null,
    });
  };

  return (
    <div className="date-filter filter-container">
      <p className="heading">
        <LuCalendar /> Date Range
      </p>

      <div className="date-range">
        <div className="date-input">
          <input
            type="date"
            name="start_date"
            onChange={(e) => {
              if (orgIsAllowedToUseSalesFilter?.exists) {
                handleDateChange(e);
              } else {
                dispatch(updateOpenSubscriptionAd({ openAd: true }));
              }
            }}
          />
          <p>{dates.start_date || "Start Date"}</p>
        </div>
        <p>to</p>
        <div className="date-input">
          <input
            type="date"
            name="end_date"
            onChange={(e) => {
              if (orgIsAllowedToUseSalesFilter?.exists) {
                handleDateChange(e);
              } else {
                dispatch(updateOpenSubscriptionAd({ openAd: true }));
              }
            }}
          />
          <p>{dates.end_date || "End Date"}</p>
        </div>
      </div>

      <div className="grid-list-dates">
        {CalenderOptions.map((el, i) => {
          return (
            <div
              className={`item ${
                selectedCalenderFilter === el.value ? "active" : ""
              }`}
              key={i}
              onClick={() => {
                if (selectedCalenderFilter === el.value) {
                  setSelectedCalenderFilter(null);
                } else {
                  setSelectedCalenderFilter(el.value);
                  clearDates();
                }
              }}
            >
              {el.label}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MethodFilter = ({
  selectedPaymentMethodFilter,
  setSelectedPaymentMethodFilter,
}) => {
  const handleToggle = (name) => {
    if (selectedPaymentMethodFilter.includes(name)) {
      const updatedMethods = selectedPaymentMethodFilter.filter(
        (el) => el !== name
      );
      setSelectedPaymentMethodFilter(updatedMethods);
    } else {
      const updatedMethods = [...selectedPaymentMethodFilter, `${name}`];
      setSelectedPaymentMethodFilter(updatedMethods);
    }
  };
  return (
    <div className="method-filter filter-container">
      <p className="heading">
        <BsCreditCard /> Payment Method
      </p>

      <div className="methods-list">
        {PaymentMethods.map((el, i) => {
          const value = selectedPaymentMethodFilter.includes(el.value);
          return (
            <div className="item" key={i}>
              <ToggleSwitch
                label={el.name}
                name={el.value}
                register={() => {}}
                value={value}
                handleChange={(name) => {
                  handleToggle(name);
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OtherFilter = ({
  setSelectedBranchFilter,
  setSelectedStatusFilter,
  selectedStatusFilter,
  selectedBranchFilter,
  branchesOptions,
}) => {
  return (
    <div className="other-filter filter-container">
      <p className="heading">
        <PiGitBranchBold /> Other Options
      </p>

      <div className="other-selects">
        <InputSelect
          options={statusOptions}
          value={selectedStatusFilter}
          register={() => {}}
          required={false}
          name="status"
          handleChange={(opt) => {
            setSelectedStatusFilter(opt);
          }}
          reset={() => {}}
          label={"Status"}
          inputErrors={{}}
        />

        <InputSelect
          options={branchesOptions}
          value={selectedBranchFilter}
          register={() => {}}
          required={false}
          name="branch"
          handleChange={(opt) => {
            setSelectedBranchFilter(opt);
          }}
          reset={() => {}}
          label={"Branch"}
          inputErrors={{}}
        />
      </div>
    </div>
  );
};
